<ng-container *transloco="let t; read: 'user'">
@if (dashboardResponse?.dashboardEnabled) {
  <div class="bg-drdpblue-200 px-5">
    <div class="min-w-full flex place-content-between place-items-center">
      <drdp-title class="mt-4" [title]="t('stateDashboard.welcome')"> {{ user.userName }}</drdp-title>
    </div>
    <span>{{ t('stateDashboard.dataUpdated', { date: ((dashboardResponse?.updatedOn + 'Z') | localizedDate: 'MM/dd/yyyy') }) }}</span>
    <div class="mt-6 flex flex-col md:flex-row gap-4 text-center">
      <div class="flex flex-col gap-4 flex-1 overflow-x-auto">
        @if (activeTiles.enrollment && childEnrollments.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl mb-4">{{ t('stateDashboard.childEnrollment') }}</h2>
            <div class="flex justify-center flex-wrap gap-4 md:gap-10">
              <div class="flex flex-col">
                <span class="text-2xl font-bold">{{ childEnrollments[0]?.total | number }}</span>
                <span>{{ dashboardResponse?.schoolYearName }}</span>
              </div>
              @for (enrollment of childEnrollments; track enrollment) {
                <div class="flex flex-col">
                  <span class="text-2xl font-bold">{{ enrollment.count | number }}</span>
                  <span>{{ enrollment.name }}</span>
                </div>
              }
            </div>
          </drdp-container>
        }
        @if (activeTiles.childByRace && raceCounts.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.childrenByRace') }}</h2>
            <h2 class="font-semibold text-xl mb-2">{{ dashboardResponse?.schoolYearName }}</h2>
            <drdp-chart [chartData]="raceCounts" chartId="raceCount" [width]="180" [height]="180" additionalClasses="max-h-[32rem]"
            [chartType]="chartType.Doughnut" [displayColorLegend]="true" legendAdditionalClasses="gap-x-5"></drdp-chart>
          </drdp-container>
        }
        @if (activeTiles.disability && disabilityCounts.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl mb-2">{{ t('stateDashboard.disabilityDemographics') }}</h2>
            <drdp-chart [chartData]="disabilityCounts" chartId="disabilityCount" [width]="180" [height]="180"
            [chartType]="chartType.Doughnut" [displayColorLegend]="true" legendAdditionalClasses="lg:pl-5 gap-x-3 lg:gap-x-6"></drdp-chart>
            <div class="mt-5">
              <a class="text-sm cursor-pointer underline text-drdpblue-300" routerLink="disability">
                {{ t('stateDashboard.exportDisabilities') }}
                <i class="uil uil-angle-right-b text-base"></i>
              </a>
            </div>
          </drdp-container>
        }
        @if (activeTiles.programByAgeGroup && programAgeGroupCounts.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.programsByAgeGroup') }}</h2>
            <h2 class="font-semibold text-xl mb-4">{{ dashboardResponse?.schoolYearName }}</h2>
            <div class="flex flex-wrap justify-center gap-y-4 md:gap-y-6">
              @for (ageGroup of programAgeGroupCounts; track ageGroup) {
                <div class="flex flex-col sm:basis-1/2">
                  <span class="text-2xl font-bold">{{ ageGroup.count | number }}</span>
                  <!-- ageGroup.name is server-supplied. -->
                  <span>{{ t('stateDashboard.programsFor', { ageGroup: ageGroup.name }) }}</span>
                </div>
              }
            </div>
            <div class="mt-10">
              <a class="text-sm cursor-pointer underline text-drdpblue-300" routerLink="programs">
                {{ t('stateDashboard.exportProgramsByAgeGroup') }}
                <i class="uil uil-angle-right-b text-base"></i>
              </a>
            </div>
          </drdp-container>
        }
        @if (activeTiles.homeLanguage && homeLanguageTotal.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.homeLanguage') }}</h2>
            <h2 class="font-semibold text-xl">{{ dashboardResponse?.schoolYearName }}</h2>
            @if (!isViewLanguage) {
              <drdp-chart additionalClasses="mb-3" [chartData]="homeLanguageTotal" chartId="homeLanguageTotal"
              [chartType]="chartType.Bar" [width]="400" [height]="180"></drdp-chart>
            }
            @if (isViewLanguage) {
              <div>
                <div class="overflow-y-auto max-h-[18rem]">
                  <drdp-legend additionalClasses="gap-x-3 pl-6" [data]="homeLanguages"></drdp-legend>
                </div>
                <div class="my-5">
                  <a class="text-sm cursor-pointer underline text-drdpblue-300" routerLink="home-languages">
                    {{ t('stateDashboard.exportHomeLanguage') }}
                    <i class="uil uil-angle-right-b text-base"></i>
                  </a>
                </div>
              </div>
            }
            <drdp-btn type="button" color="outline" (click)="onViewLanguageClick()">{{ isViewLanguage ? t('stateDashboard.closeLanguages') : t('stateDashboard.viewLanguages') }}</drdp-btn>
          </drdp-container>
        }
      </div>
      <div class="flex flex-col gap-4 flex-1 overflow-x-auto">
        @if (activeTiles.childInSped && childrenInSped) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.childrenInSpecialEducation') }}</h2>
            <h2 class="font-semibold text-xl mb-4">{{ dashboardResponse?.schoolYearName }}</h2>
            <div class="flex justify-center flex-wrap gap-4 md:gap-x-10">
              <div class="flex flex-col">
                <span class="text-2xl font-bold">{{ childrenInSped.iepStudents | number }}</span>
                <span>{{ t('stateDashboard.childrenWithIfspIep') }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-2xl font-bold">{{ childrenInSped.exitingStudents | number }}</span>
                <a routerLink="special-education" class="text-drdpblue-300 underline">
                  <span>{{ t('stateDashboard.anticipatedChildrenExiting') }}</span>
                  <i class="uil uil-angle-right"></i>
                </a>
              </div>
            </div>
          </drdp-container>
        }
        @if (activeTiles.genderDemographic && genderDemographics.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl mb-4">{{ t('stateDashboard.genderDemographics', { schoolYear: dashboardResponse?.schoolYearName }) }}</h2>
            <div class="flex justify-center flex-wrap gap-4 md:gap-8">
              @for (gender of genderDemographics; track gender) {
                <drdp-chart [chartData]="[gender]" [chartId]="gender.name"
                [chartType]="chartType.Doughnut"></drdp-chart>
              }
            </div>
          </drdp-container>
        }
        @if (activeTiles.ageGroup && ageGroupCounts.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.ageGroupInstrument') }}</h2>
            <h2 class="font-semibold text-xl mb-4">{{ t('stateDashboard.currentRatingPeriod') }}</h2>
            <div class="grid sm:grid-cols-2 gap-4">
              @for (rpAgeGroup of ageGroupCounts; track rpAgeGroup) {
                <div class="flex flex-col">
                  <span class="text-2xl font-bold">{{ rpAgeGroup.count | number }}</span>
                  <span>{{ rpAgeGroup.name }}</span>
                  <span>{{ rpAgeGroup.subName }}</span>
                </div>
              }
            </div>
          </drdp-container>
        }
        @if (activeTiles.rating && ratingsCounts.length) {
          <drdp-container>
            <h2 class="font-semibold text-xl">{{ t('stateDashboard.childRatings') }}</h2>
            <h2 class="font-semibold text-xl">{{ dashboardResponse?.schoolYearName }}</h2>
            <drdp-chart additionalClasses="mb-3" [chartData]="ratingsCounts" chartId="ratingsCounts" [chartType]="chartType.Bar"
            [width]="300" [height]="500" [isHorizontalBarChart]="true" labelsSplitAt="ratings"></drdp-chart>
            <div class="mt-5">
            <a class="text-sm cursor-pointer underline text-drdpblue-300" routerLink="child-ratings">
              {{ t('stateDashboard.exportChildRatings') }}
              <i class="uil uil-angle-right-b text-base"></i>
            </a>
          </div>
        </drdp-container>
      }
      @if (activeTiles.childByIep && iepIfspGroups.length) {
        <drdp-container>
          <h2 class="font-semibold text-xl mb-4">{{ t('stateDashboard.childrenWithIeps') }}</h2>
          <div class="flex flex-col">
            @for (group of iepIfspGroups; track group.name) {
              <div class="border-t border-drdpgrey-25">
                <div class="flex justify-between font-bold px-4 py-2" [ngClass]="getIepIfspColorClasses(group.color).text">
                  <span>{{ group.name }}</span>
                  <span>{{ group.count }}</span>
                </div>
                @if (group.items?.length) {
                  <div class="pb-4 pl-4">
                    @for (item of group.items!; track item.subName; let last = $last) {
                      <div class="px-4 border-l-4" [ngClass]="[getIepIfspColorClasses(group.color).bg, getIepIfspColorClasses(group.color).border]">
                        <div class="flex justify-between py-1.5" [ngClass]="{ 'border-b border-drdpgrey-25': !last }">
                          <span>{{ item.subName }}</span>
                          <span [ngClass]="getIepIfspColorClasses(group.color).text">{{ item.count | number }}</span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </drdp-container>
      }
      @if (activeTiles.ageBasedOnDob && ageCounts.length) {
        <drdp-container>
          <h2 class="font-semibold text-xl mb-2">{{ t('stateDashboard.ageBasedOnDob') }}</h2>
          <drdp-chart [chartData]="ageCounts" chartId="ageCount" [width]="160" [height]="160" legendAdditionalClasses="gap-x-10"
          [chartType]="chartType.Doughnut" [displayColorLegend]="true"></drdp-chart>
        </drdp-container>
      }
    </div>
  </div>
</div>
}
</ng-container>
